import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import userService from "@/services/api/user";
import type { GetUsersParams, User } from "@/services/api/user";

export interface UserState {
  users: User[];
  totalUsers: number;
  approvedCount: number;
  subscriptionRenewalsCount: number;
  isFetching: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  totalUsers: 0,
  approvedCount: 0,
  subscriptionRenewalsCount: 0,
  isFetching: false,
  error: null,
};

export const getUsers = createAsyncThunk(
  "user/getUsers",
  async (params: GetUsersParams | undefined) => userService.getUsers(params),
);

export const getAdminUsers = createAsyncThunk(
  "user/getAdminUsers",
  async (params: GetUsersParams | undefined) => userService.getAdminUsers(params),
);

const handlePending = (state: UserState) => {
  state.isFetching = true;
  state.error = null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleFulfilled = (state: UserState, action: any) => {
  state.isFetching = false;
  const rawResults = action.payload?.data?.results;
  const rawList = Array.isArray(rawResults)
    ? rawResults
    : Array.isArray(action.payload?.data)
      ? action.payload.data
      : Array.isArray(action.payload?.results)
        ? action.payload.results
        : [];
  state.users = rawList;
  state.totalUsers = action.payload?.data?.totalRecord ?? action.payload?.totalRecord ?? rawList.length;
  state.approvedCount = action.payload?.data?.approvedCount ?? 0;
  state.subscriptionRenewalsCount = action.payload?.data?.subscriptionRenewalsCount ?? 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleRejected = (state: UserState, action: any) => {
  state.isFetching = false;
  state.users = [];
  state.approvedCount = 0;
  state.subscriptionRenewalsCount = 0;
  state.error = action.error?.message ?? "Failed to load users";
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, handlePending)
      .addCase(getUsers.fulfilled, handleFulfilled)
      .addCase(getUsers.rejected, handleRejected)
      .addCase(getAdminUsers.pending, handlePending)
      .addCase(getAdminUsers.fulfilled, handleFulfilled)
      .addCase(getAdminUsers.rejected, handleRejected);
  },
});

export default userSlice.reducer;
